import { Vector } from './Vector'
import { matrix } from './Matrix'
import { Physics } from './Physics'
import { Plane } from './Plane';
import { SteeringState } from './Steering';

// Rendering interfaces - high-level module defines abstractions (DIP)
export interface IUpdateRenderable {
    /** 
     * Each component is the half extent of the AABB
     * The magnitude and direction of this vector represents the longest distance 
     * from the center of the object to its edge
    */
    readonly radius: Vector;
    update(position: Vector, heading?: Vector): void;
}

export interface IDebugRenderable {
    debug(position: Vector): void;
}

// For backward compatibility - objects that implement both interfaces
export interface IRenderable extends IUpdateRenderable, IDebugRenderable {}

export class StationaryGameObject {
    private renderable: IRenderable;
    readonly position: Vector;
    readonly collisionPlanes?: Plane[];
    readonly planeRenderables?: IDebugRenderable[];
    get halfExtents(): Vector {
        return this.renderable.radius;
    }

    constructor(renderable: IRenderable, position: Vector, collisionPlanes?: Plane[], planeRenderables?: IDebugRenderable[]) {
        this.renderable = renderable;
        this.position = position;
        this.collisionPlanes = collisionPlanes;
        this.planeRenderables = planeRenderables;
        this.updateSprite(false);
    }

    updateSprite(isDebugEnabled: boolean) {
        this.renderable.update(this.position);
        if (isDebugEnabled) {
            this.renderable.debug(this.position);
            // Update debug plane visualizations
            if (this.planeRenderables) {
                for (const planeRenderable of this.planeRenderables) {
                    planeRenderable.debug(this.position);
                }
            }
        }
    }
}

export class SignGameObject {
    private readonly renderable: IRenderable;
    readonly position: Vector;
    readonly limit: number;
    get radius(): Vector {
        return this.renderable.radius;
    }

    constructor(renderable: IRenderable, position: Vector, limit: number) {
        this.renderable = renderable;
        this.position = position;
        this.limit = limit;
    }

    updateSprite(isDebugEnabled: boolean) {
        this.renderable.update(this.position);
        if (isDebugEnabled) {
            this.renderable.debug(this.position);
        }
    }
}

export class CarGameObject {
    FromPosition: Vector;
    ToPosition: Vector;
    FromVelocity: Vector;
    ToVelocity: Vector;
    mass: number;
    Movement: Vector;
    // Crashed: boolean = false;

    get halfExtents(): Vector {
        return this.renderable.radius;
    }
    /**
     * Project the half extents onto the plane normal to get the radius of the object in the direction of the plane normal
     * i.e. if the normal is (1,0,0) then we want the x component of the half extends
     * if the normal is (0,1,0) then we want the y component of the half extends
     * if the normal is aligned with radius vector then it would be the full extend of the radius vector
     */
    radius(plane: Plane): number {
        const normalised = Vector.normalise(plane.normal);
        return Vector.dotProduct(this.renderable.radius, new Vector(Math.abs(normalised.x), Math.abs(normalised.y), Math.abs(normalised.z)));
    }
    Steering: SteeringState;
    private readonly renderable: IRenderable;
    private heading: Vector;

    constructor(renderable: IRenderable, position: Vector) {
        this.renderable = renderable;
        this.Steering = new SteeringState();
        this.FromPosition = position; 
        this.ToPosition = position; 
        this.FromVelocity = new Vector(0, 0, 0);
        this.ToVelocity = new Vector(0, 0, 0);
        this.mass = 10;
        this.Movement = new Vector(0, 0, 0);
        this.heading = new Vector(1, 0, 0);
    }

    private position(interpolationValue: number) {
        const interpolationVector = Vector.scale(this.Movement, interpolationValue);
        return Vector.add(this.FromPosition, interpolationVector);
    }

    updateSprite(interpolationValue: number, isDebugEnabled: boolean): Vector {
        const interpolatedPosition = this.position(interpolationValue);
        const headingSpeedSquared = Vector.dotProduct(this.ToVelocity, this.ToVelocity);
        if (headingSpeedSquared > 1e-12) {
            this.heading = Vector.normalise(this.ToVelocity);
        }

        this.renderable.update(interpolatedPosition, this.heading);
        if (isDebugEnabled) {
            this.renderable.debug(interpolatedPosition);
        }
        return interpolatedPosition;
    }

    // Move by applying a force and optionally a turn matrix
    // Move(force: Vector, turn?: matrix) {
    Move(forceMagnitude: number, turn?: matrix) {
        // force should be applied in the direction of the current velocity, but with magnitude of the input force
        let force: Vector;
        if (Vector.magnitude(this.ToVelocity) > 0) {
            force = Vector.scale(Vector.normalise(this.ToVelocity), forceMagnitude);
        } else {
            force = new Vector(forceMagnitude, 0, 0);
        }

        // force = Vector.Scale(Vector.Normalise(this.ToVelocity), Vector.Magnitude(force));

        this.FromPosition = this.ToPosition;
        this.FromVelocity = this.ToVelocity;

        const acceleration = Vector.scale(force, 1 / this.mass);

        this.ToVelocity = Physics.NewVelocity(this.FromVelocity, acceleration, 33);
        if (turn != null)
            this.ToVelocity = turn.multiply(this.ToVelocity);
        else {
            if (this.Steering.isStraight()) {
                const straightSpeed = Vector.magnitude(this.ToVelocity);
                const isReversing = this.ToVelocity.x < 0;
                this.ToVelocity = isReversing
                    ? new Vector(0, 0, 0)
                    : new Vector(straightSpeed, 0, 0);
            }
        }

        this.ToPosition = Physics.NewPosition(this.FromPosition, this.FromVelocity, acceleration, 33);
        this.Movement = Vector.subtract(this.ToPosition, this.FromPosition);
    }
}
